import { useTranslation } from 'react-i18next';
import { BarChart3, Facebook, Instagram, Linkedin, Music2, Youtube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useSocialLinkingPlatforms } from '@/hooks/use-social-linking-platforms';
import type { SocialLinkingPlatform } from '@/lib/account-settings/visibleSocialLinkingPlatforms';

type AnalyticsEmptyStateProps = {
  onConnect: (platform: SocialLinkingPlatform) => void;
};

const platforms: Array<{ platform: SocialLinkingPlatform; icon: typeof Instagram }> = [
  { platform: 'instagram', icon: Instagram },
  { platform: 'tiktok', icon: Music2 },
  { platform: 'youtube', icon: Youtube },
  { platform: 'linkedin', icon: Linkedin },
  { platform: 'facebook', icon: Facebook },
];

export const AnalyticsEmptyState = ({ onConnect }: AnalyticsEmptyStateProps) => {
  const { t } = useTranslation();
  const visiblePlatforms = useSocialLinkingPlatforms();

  return (
    <Card className="mx-auto w-full max-w-2xl overflow-hidden border-border/70 shadow-sm">
      <div className="h-1 bg-gradient-to-r from-transparent via-primary/70 to-transparent" />
      <CardContent className="flex flex-col items-center px-6 py-12 text-center sm:px-12 sm:py-16">
        <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary">
          <BarChart3 className="h-6 w-6" />
        </div>
        <h2 className="text-xl font-semibold tracking-tight">{t('analytics.empty.title')}</h2>
        <p className="mt-3 max-w-xl text-sm leading-6 text-muted-foreground">
          {t('analytics.empty.description')}
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-2">
          {platforms.filter(({ platform }) => visiblePlatforms.includes(platform)).map(({ platform, icon: Icon }) => (
            <Button
              key={platform}
              type="button"
              variant="outline"
              onClick={() => onConnect(platform)}
              className="border-border/80"
            >
              <Icon className="h-4 w-4" />
              {t(`analytics.empty.connect${platform.charAt(0).toUpperCase()}${platform.slice(1)}`)}
            </Button>
          ))}
        </div>
        <p className="mt-5 text-xs leading-5 text-muted-foreground">{t('analytics.empty.officialDataNote')}</p>
      </CardContent>
    </Card>
  );
};
